// pages/stock/StockHome.js
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Alert,
    Box,
    SimpleGrid,
    Skeleton,
} from '@chakra-ui/react';

// Organized imports
import { useStockInfo } from '../../hooks/useStockInfo';
import { useSecFilingsData } from '../../hooks/useSecFilingsData';
import { useReverseDcfSaves } from '../../hooks/useReverseDcfSaves';
import { useDcfSaves } from '../../hooks/useDcfSaves';
import { usePublicStockMetricCount } from '../../hooks/usePublicStockMetricCount';
import { getStockPrice, getStockPriceHistory } from '../../api';
import StockHomeDashboard, { buildStockHomeExternalLinks } from './StockHomeDashboard';

const PRICE_RANGES = {
    '1m': { label: '1M', days: 31 },
    '6m': { label: '6M', days: 183 },
    '1y': { label: '1Y', days: 366 },
    '5y': { label: '5Y', days: 1827 },
};

const toDateParam = (date) => date.toISOString().slice(0, 10);

export const buildStockHomePriceRange = (range, now = new Date()) => {
    const config = PRICE_RANGES[range] || PRICE_RANGES['1y'];
    const end = new Date(now);
    const start = new Date(now);
    start.setUTCDate(start.getUTCDate() - config.days);
    return { startDate: toDateParam(start), endDate: toDateParam(end) };
};

const sortFilingsByDateDesc = (filings) =>
    filings.toSorted((a, b) => {
        const dateA = a?.filingDateValue || Date.parse(a?.filingDate || '') || 0;
        const dateB = b?.filingDateValue || Date.parse(b?.filingDate || '') || 0;
        return dateB - dateA;
    });

const isInsiderForm = (filing) => {
    const formType = String(filing?.formTypeKey || filing?.formType || '')
        .trim()
        .toUpperCase()
        .replace(/^FORM\s+/, '');

    return (
        formType === '3' ||
        formType === '4' ||
        formType === '5' ||
        formType === '144' ||
        formType.startsWith('3/') ||
        formType.startsWith('4/') ||
        formType.startsWith('5/')
    );
};

function StockHome() {
    const { ticker } = useParams();

    // Use safe defaults to ensure hooks are always called with valid values
    const upperCaseTicker = ticker ? ticker.toUpperCase() : '';
    const lowerCaseTicker = ticker ? ticker.toLowerCase() : '';
    const { data: stockInfo, loading, error } = useStockInfo(upperCaseTicker);
    const { isAuthenticated, isLoading: isAuthLoading, getAccessTokenSilently } = useAuth0();
    const publicMetricsCount = usePublicStockMetricCount(upperCaseTicker);
    const [priceRange, setPriceRange] = useState('1y');
    const [priceHistory, setPriceHistory] = useState({
        prices: [],
        loading: true,
        error: null,
        status: null,
        message: null,
    });
    const [stockPrice, setStockPrice] = useState({ data: null, loading: true });
    const apiUrl = useMemo(() => (process.env.REACT_APP_API_URL || '').replace(/\/+$/, ''), []);
    const secApiBaseUrl = apiUrl;
    const { filings, isFetching: filingsLoading, error: filingsError } = useSecFilingsData(upperCaseTicker, secApiBaseUrl);
    const {
        saves: reverseDcfSaves,
        loading: reverseDcfLoading,
        error: reverseDcfError,
    } = useReverseDcfSaves({
        apiUrl,
        getAccessTokenSilently,
        isAuthenticated,
        ticker: upperCaseTicker,
    });
    const {
        saves: dcfSaves,
        loading: dcfLoading,
        error: dcfError,
    } = useDcfSaves({
        apiUrl,
        getAccessTokenSilently,
        isAuthenticated,
        ticker: upperCaseTicker,
    });

    const sortedFilings = useMemo(() => sortFilingsByDateDesc(filings), [filings]);
    const latestFilings = useMemo(() => sortedFilings.slice(0, 5), [sortedFilings]);
    const insiderFilings = useMemo(
        () => sortedFilings.filter(isInsiderForm).slice(0, 3),
        [sortedFilings]
    );

    const externalLinks = useMemo(() => {
        return buildStockHomeExternalLinks({ lowerCaseTicker, stockInfo, upperCaseTicker });
    }, [lowerCaseTicker, upperCaseTicker, stockInfo]);

    useEffect(() => {
        if (!upperCaseTicker) return undefined;
        const controller = new AbortController();
        const { startDate, endDate } = buildStockHomePriceRange(priceRange);

        setPriceHistory({
            prices: [],
            loading: true,
            error: null,
            status: null,
            message: null,
        });
        getStockPriceHistory({
            ticker: upperCaseTicker,
            startDate,
            endDate,
            signal: controller.signal,
        }).then((payload) => {
            setPriceHistory({
                prices: payload?.prices || [],
                loading: false,
                error: null,
                status: payload?.price_status || null,
                message: payload?.price_message || null,
            });
        }).catch((requestError) => {
            if (requestError?.name === 'AbortError') return;
            setPriceHistory((current) => ({
                ...current,
                loading: false,
                error: requestError,
            }));
        });

        return () => controller.abort();
    }, [priceRange, upperCaseTicker]);

    useEffect(() => {
        if (!upperCaseTicker) return undefined;
        const controller = new AbortController();
        setStockPrice({ data: null, loading: true });
        getStockPrice({ ticker: upperCaseTicker, signal: controller.signal })
            .then((data) => setStockPrice({ data, loading: false }))
            .catch((requestError) => {
                if (requestError?.name === 'AbortError') return;
                setStockPrice({ data: null, loading: false });
            });
        return () => controller.abort();
    }, [upperCaseTicker]);

    const routes = useMemo(() => {
        const stockBase = `/stock/${lowerCaseTicker}`;
        return {
            analysis: `${stockBase}/analysis`,
            reverseDcf: `${stockBase}/reverse-dcf`,
            charts: `${stockBase}/charts`,
            snapshot: `${stockBase}/snapshot`,
            financials: `${stockBase}/financials/balance-sheet`,
            filings: `${stockBase}/sec-filings`,
            publicMetrics: `/stocks/${lowerCaseTicker}/metrics`,
        };
    }, [lowerCaseTicker]);

    // Handle missing ticker gracefully (after hooks are called)
    if (!ticker) {
        return (
            <Box p={4}>
                <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Content>
                        Invalid ticker symbol. Please provide a valid stock ticker.
                    </Alert.Content>
                </Alert.Root>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box p={4}>
                <Skeleton height="40px" width="300px" mb={4} />
                <Skeleton height="20px" width="400px" mb={6} />
                <SimpleGrid columns={{ base: 1, sm: 2, xl: 3 }} gap={4}>
                    <Skeleton height="150px" />
                    <Skeleton height="150px" />
                    <Skeleton height="150px" />
                    <Skeleton height="150px" />
                    <Skeleton height="150px" />
                </SimpleGrid>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={4}>
                <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Content>
                        {error}
                    </Alert.Content>
                </Alert.Root>
            </Box>
        );
    }
    
    // Handle case where stockInfo is null/undefined (shouldn't happen if error is set, but be defensive)
    if (!stockInfo) {
        return (
            <Box p={4}>
                <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Content>
                        Could not load stock information for ticker "{upperCaseTicker}". Please verify the ticker symbol is correct.
                    </Alert.Content>
                </Alert.Root>
            </Box>
        );
    }
    
    return (
        <StockHomeDashboard
            externalLinks={externalLinks}
            filingsError={filingsError}
            filingsLoading={filingsLoading}
            insiderFilings={insiderFilings}
            isAuthenticated={isAuthenticated}
            isAuthLoading={isAuthLoading}
            latestFilings={latestFilings}
            publicMetricsCount={publicMetricsCount}
            priceHistory={priceHistory}
            priceRange={priceRange}
            priceRangeOptions={PRICE_RANGES}
            setPriceRange={setPriceRange}
            stockPrice={stockPrice}
            dcfError={dcfError}
            dcfLoading={dcfLoading}
            dcfSaves={dcfSaves}
            reverseDcfError={reverseDcfError}
            reverseDcfLoading={reverseDcfLoading}
            reverseDcfSaves={reverseDcfSaves}
            routes={routes}
            stockInfo={stockInfo}
            ticker={ticker}
            upperCaseTicker={upperCaseTicker}
        />
    );
}

export default StockHome;
